import { Box, Text } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { tryCatch } from '@wix/funnel-utils'
import React, { FC, useCallback, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { usePageLogger } from '../../../bi/mfi'
import { LARGE_DESKTOP } from '../../../breakpoints'
import { Button } from '../../reusable/Button'
import { RichText } from '../../reusable/RichText'
import { WixStudioLogoWithBack } from '../../reusable/WixStudioLogoWithBack'
import { WixStudioTemplate } from '../../reusable/WixStudioTemplate/WixStudioTemplate'
import { WixStudioTemplateSidePanel } from '../../reusable/WixStudioTemplate/WixStudioTemplateSidePanel'
import { Content } from './Content'
import { dataHooks } from './WixStudioBulletSplitPantone.dataHooks'
import s from './WixStudioBulletSplitPantone.scss'

export interface WixStudioBulletSplitProps {
  markAsWixStudioAccount: () => Promise<void>
  unmarkAsWixStudioAccount: () => Promise<void>
}

export const WixStudioBulletSplitPantone: FC<WixStudioBulletSplitProps> = ({
  markAsWixStudioAccount,
  unmarkAsWixStudioAccount,
}) => {
  const { content } = useContent<Content>()
  const { questionKey, options } = content.question
  const next = useNext()
  const pageLogger = usePageLogger()
  const [isPageLoading] = usePageLoading()
  const isLargeDesktopScreen = useMediaQuery(LARGE_DESKTOP)
  const [isPrimaryButtonClicked, setPrimaryButtonClicked] = useState<boolean>(false)

  const [primaryOption] = options
  const handlePrimaryOptionClick = useCallback(async () => {
    if (isPageLoading) {
      return
    }
    setPrimaryButtonClicked(true)
    void pageLogger.answer({ answer: primaryOption.optionKey })
    if (primaryOption.markAsStudio) {
      const [error] = await tryCatch(markAsWixStudioAccount)
      if (error) {
        window.open('https://www.wix.com/studio/templates', '_self')
        return
      }
    } else if (primaryOption.unmarkAsStudio) {
      const [error] = await tryCatch(unmarkAsWixStudioAccount)
      if (error) {
        window.open('https://www.wix.com/studio/templates', '_self')
        return
      }
    }
    next({ [questionKey]: { values: [primaryOption.optionKey] } })
  }, [isPageLoading, markAsWixStudioAccount, next, pageLogger, primaryOption, questionKey])

  return (
    <WixStudioTemplate
      logo={<WixStudioLogoWithBack />}
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      sidePanel={<WixStudioTemplateSidePanel className={s.sidePanel} content={[content.sidePanel]} />}
      title={
        <Box maxWidth="500px" marginBottom="SP4">
          {content.title}
        </Box>
      }
      bottomNote={
        content.note && (
          <Box maxWidth="370px">
            <RichText size="small" secondary dataHook={dataHooks.note()}>
              {content.note}
            </RichText>
          </Box>
        )
      }
    >
      <Box marginBottom="SP7" maxWidth="500px">
        <Text listStyle="checkmark">{content.bullet}</Text>
      </Box>
      <Box verticalAlign="middle">
        <Button
          label={primaryOption.label}
          skin="standard"
          isLoading={isPrimaryButtonClicked}
          onClick={handlePrimaryOptionClick}
          size={isLargeDesktopScreen ? 'large' : 'medium'}
          dataHook={dataHooks.mainButton()}
        />
      </Box>
    </WixStudioTemplate>
  )
}
